import { RiskFactorService } from '@/modules/global-settings/shared/services/risk-factor.service';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';

// Creates a new instance of the RiskFactorService class
const riskFactorService = new RiskFactorService();
// Export module state
export const state = {
  riskFactors: [],
  subRiskFactors: [],
  riskFactorById: {},
  riskFactorByModuleName: {},
  riskFactorByName: {}
};
// Export module mutations
export const mutations = {
  ...make.mutations(state)
};
// Export module actions
export const actions = {
  /**
   * Method used to fetch all risk factors from the API
   * @returns {Array} - Risk factors
   */
  async getRiskFactors({ commit }) {
    try {
      const response = await riskFactorService.getRiskFactors();
      commit('SET_RISK_FACTORS', response.data);
      return response.data;
    } catch (error) {
      Logger.error('getting risk factors', error);
      throw error;
    }
  },

  /**
   * Retrieves a paginated list of risk factors.
   * Does not commit to the store; returns data directly to the component.
   */
  async getPaginatedRiskFactors(_, params) {
    try {
      const { data } = await riskFactorService.getPaginatedRiskFactors(params);
      return data; 
    } catch (error) {
      Logger.error('Getting paginated risk factors', error);
      return { data: [], total: 0 }; 
    }
  },

  /**
   * Method used to fetch a risk factor by id from the API
   * @param {Object} context - Vuex commit
   * @param {String} id
   * @returns {Object} Risk factors
   */
  async getRiskFactorById({ commit }, id) {
    try {
      const response = await riskFactorService.getRiskFactorById(id);
      commit('SET_RISK_FACTOR_BY_ID', response.data);
      return response.data;
    } catch (error) {
      Logger.error('getting risk factor by id', error);
      throw error;
    }
  },
  /**
   * Method used to fetch a risk factor by moduleName from the API
   * @param {Object} context - Vuex commit
   * @param {String} moduleName
   * @returns {Object} Risk factors
   */
  async getRiskFactorByModuleName({ commit }, moduleName) {
    try {
      const response = await riskFactorService.getRiskFactorByModuleName(
        moduleName
      );
      commit('SET_RISK_FACTOR_BY_MODULE_NAME', response.data);
      return response.data;
    } catch (error) {
      Logger.error('getting risk factor by module name', error);
      throw error;
    }
  },
  /**
   * Method used to fetch a risk factor by name from the API
   * @param String - name
   * @returns Object - Risk factors
   */
  async getRiskFactorByName({ commit }, name) {
    try {
      const response = await riskFactorService.getRiskFactorByName(name);
      commit('SET_RISK_FACTOR_BY_NAME', response.data);
      return response.data;
    } catch (error) {
      Logger.error('getting risk factor by name', error);
      throw error;
    }
  },
  /**
   * Method used to update a risk factor
   * @param Object - risk factor params
   * @returns Object Risk factors
   */
  async updateRiskFactor(_, payload) {
    try {
      const response = await riskFactorService.updateRiskFactor(payload);
      return response.data;
    } catch (error) {
      Logger.error('updating risk factor ', error);
      throw error;
    }
  },
  /**
   * Method used to create a sub risk factor
   * @param Object - risk factor params
   * @returns Object Risk factors
   */
  async createSubRiskFactor(_, payload) {
    try {
      const response = await riskFactorService.createSubRiskFactor(payload);
      return response.data;
    } catch (error) {
      Logger.error('creating sub risk factor ', error);
      throw error;
    }
  },
  /**
   * Method used to fetch all sub risk factors from the API
   * @returns Array - Sub Risk factors
   */
  async getSubRiskFactors({ commit }) {
    try {
      const response = await riskFactorService.getSubRiskFactors();
      commit('SET_SUB_RISK_FACTORS', response.data);
      return response.data;
    } catch (error) {
      Logger.error('getting risk factors', error);
      throw error;
    }
  },
  /**
   * Method used to create a sub risk factor
   * @param Object - risk factor params
   * @returns Object Risk factors
   */
  async updateSubRiskFactor(_, payload) {
    try {
      const response = await riskFactorService.updateSubRiskFactor(payload);
      return response.data;
    } catch (error) {
      Logger.error('updating sub risk factor ', error);
      throw error;
    }
  },
  /**
   * Method used to delete a sub risk factor
   * @param String - id
   * @returns Object Sub Risk factors
   */
  async deleteSubRiskFactor(_, id) {
    try {
      const response = await riskFactorService.deleteSubRiskFactor(id);
      return response.data;
    } catch (error) {
      Logger.error('deleting sub risk factor ', error);
      throw error;
    }
  },
  /**
   * Method used to update risk factor list
   * This method will call the endpoint that will make changes to stale risk factors in the DB
   * @returns Object Risk factors
   */
   async updateSubRiskFactorRanges(_, payload) {
    try {
      const response = await riskFactorService.batchUpdateSubRiskFactorRanges(payload);
      return response.data;
    } catch (error) {
      Logger.error('batch updating sub risk factor ranges', error);
      throw error;
    }
  },
  async updateRiskFactorList() {
    try {
      const response = await riskFactorService.updateRiskFactorList();
      return response.data;
    } catch (error) {
      Logger.error('updating risk factor list ', error);
      throw error;
    }
  }
};

export const getters = {
  ...make.getters(state),
  activeOnBoarderRiskFactorByModuleName(state) {
    return state.riskFactorByModuleName.filter((r) => {
      return r.modules.find((m) => {
        return m.riskFactorIsActive == true && m.moduleName == 'On-Boarder';
      });
    });
  },
  getActiveRiskFactorsByModuleName: (state) => (mod) => {
    return state.riskFactors.filter((r) => {
      return r.modules.find((m) => {
        return m.riskFactorIsActive == true && m.moduleName == mod;
      });
    });
  },
  singleRiskFactorByName: (state) => (name) => {
    return state.riskFactors.find((r) => (r.riskFactorName = name));
  }
};
